import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';
import useResizeObserver from 'use-resize-observer';
import { VideoStats } from '../cockpit/Cockpit';
import VolumeControls from '../party/VolumeControls';

const VIDEO_DIMENSIONS = 16 / 9;

const defaultHlsConfig = {
  // liveSyncDurationCount: 3,
  // liveMaxLatencyDurationCount: 30,
  manifestLoadingMaxRetry: 100,
  manifestLoadingRetryDelay: 5000,
};

// function waitUntilRemoteStreamStartsFlowing() {
//   if (
//     !(
//       remote_video.readyState <= HTMLMediaElement.HAVE_CURRENT_DATA ||
//       remote_video.paused ||
//       remote_video.currentTime <= 0
//     )
//   ) {
//     // remote stream started flowing!
//   } else setTimeout(waitUntilRemoteStreamStartsFlowing, 50);
// }

export const getUrl = (id: string) =>
  `${process.env.REACT_APP_ANT_URL_BASE}streams/${id}.m3u8?token=null`;

const BackroomHLSPlayer = ({
  opacity = 1,
  onLoadComplete,
  secondary,
  videoId,
  hlsConfig,
  height,
  controls,
  poster = '/images/video_loading.png',
  videoProps,
  style,
  volume,
  muted,
  onStatsUpdated,
  fullscreen,
}: {
  onLoadComplete?: (videoEl: HTMLVideoElement) => void;
  secondary?: boolean;
  videoId?: string | null;
  hlsConfig?: object;
  controls?: boolean;
  height?: number | string;
  poster?: string;
  videoProps?: object;
  style?: object;
  volume: number;
  opacity?: number;
  muted: boolean;
  onStatsUpdated?: (obj: VideoStats) => void;
  fullscreen?: boolean;
}) => {
  const hlsRef = useRef<null | Hls>(null);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const { ref, height: measuredHeight = 0 } = useResizeObserver<HTMLDivElement>();

  useEffect(() => {
    if (!videoId) {
      return;
    }

    let playingState: 'init' | 'ended' | 'loading' | 'playing' = 'init';
    let timeout: NodeJS.Timeout | null = null;
    let hasStarted = false;

    const onPlay = () => {
      if (!hasStarted) {
        hasStarted = true;
        if (onLoadComplete) {
          console.log('load complete: a', videoId);
          onLoadComplete(videoElementRef.current!);
        }
      }
    };
    const video = videoElementRef.current;

    const tryToPlay = () => {
      if (hlsRef.current && playingState === 'ended') {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      if (playingState === 'init' || playingState === 'ended') {
        hlsRef.current = new Hls({
          ...defaultHlsConfig,
          ...hlsConfig,
        });

        const hls = hlsRef.current;

        hls.loadSource(getUrl(videoId));
        playingState = 'loading';

        if (video) {
          hls.attachMedia(video);
          video.muted = true;
        }

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          playingState = 'playing';
          clearTimeout(timeout as NodeJS.Timeout);
          if (video) {
            video.play();
            video.muted = muted;
          }
        });

        video?.addEventListener('playing', onPlay);

        hls.on(Hls.Events.ERROR, function (event, data) {
          if (data.fatal) {
            playingState = 'ended';
            clearTimeout(timeout as NodeJS.Timeout);
            timeout = setTimeout(tryToPlay, 2000);
          }
        });

        hls.on(Hls.Events.FRAG_BUFFERED, function (event, data) {
          if (onStatsUpdated) {
            const quality = videoElementRef.current?.getVideoPlaybackQuality();
            onStatsUpdated({
              // @ts-ignore
              bandwidth: Math.round(data.stats.bwEstimate / 100000),
              originalHeight: videoElementRef.current?.videoHeight || 0,
              originalWidth: videoElementRef.current?.videoWidth || 0,
              // @ts-ignore
              corruptedVideoFrames: quality.corruptedVideoFrames,
              creationTime: quality?.creationTime,
              droppedVideoFrames: quality?.droppedVideoFrames,
              totalVideoFrames: quality?.totalVideoFrames,
            });
          }
        });

        hls.on(Hls.Events.BUFFER_EOS, function (event, data) {
          playingState = 'ended';
          // 10 seconds to allow the HLS chunk to clear
          // otherwise, we would load the final chunk again
          clearTimeout(timeout as NodeJS.Timeout);
          timeout = setTimeout(tryToPlay, 10000);
        });
      }
    };

    timeout = setTimeout(tryToPlay, 0);

    return () => {
      video?.removeEventListener('playing', onPlay);
      clearTimeout(timeout as NodeJS.Timeout);
      if (hlsRef.current) {
        hlsRef.current?.destroy();
        hlsRef.current = null;
      }
    };
  }, [hlsConfig, videoId, onLoadComplete, muted, onStatsUpdated]);

  useEffect(() => {
    if (videoElementRef.current) {
      videoElementRef.current.volume = volume;
    }
  }, [videoElementRef, volume, videoId]);

  let extraStyle: React.CSSProperties = {};
  if (secondary && opacity !== 1) {
    extraStyle.position = 'absolute';
  }

  if (!opacity || !videoId) {
    extraStyle.display = 'none';
  }

  let props: React.DetailedHTMLProps<
    React.VideoHTMLAttributes<HTMLVideoElement>,
    HTMLVideoElement
  > = {};
  if (fullscreen) {
    if (window.innerWidth / window.innerHeight < VIDEO_DIMENSIONS) {
      props.height = '100%';
    } else {
      props.width = '100%';
    }
  } else {
    let width;
    if (typeof height === 'number') {
      width = height * VIDEO_DIMENSIONS;
    } else {
      width = measuredHeight * VIDEO_DIMENSIONS;
    }
    props.width = width;
  }

  return (
    <div ref={ref} className='player-area' style={{ width: '100%', height: '100%', ...extraStyle }}>
      <div style={{ display: 'flex' }}></div>
      <video
        ref={videoElementRef}
        className='hls-player'
        controls={controls}
        // height={height}
        poster={poster}
        style={{ ...style, opacity }}
        disablePictureInPicture
        {...videoProps}
        {...props}></video>
      {/* <div style={{ display: 'flex', height: '100%' }}>
        <VolumeControls />
      </div> */}
      {/* <div style={{width: 50, height: '100%'}}><div style={{height: 100 * videoElementRef.current?.volume }}></div></div> */}
    </div>
  );
};

export default BackroomHLSPlayer;
